import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './PricingPage.css';
import Navbar from './Navbar';
import Footer from './Footer';
import api from '../api';
import { AuthContext } from '../Login/AuthContext';
import LoadingButton from '../UI/LoadingButton';
import { usePricing } from '../Payment/PricingContext';
import DealfioulBanner from './DealfioulBanner';

const PricingPage = () => {
    const { prices, currency, loading, formatPrice } = usePricing();
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    const calculateSavings = (periodPrice, monthlyPrice, months) => {
        if (!periodPrice || !monthlyPrice) return 0;
        const totalMonthlyPrice = monthlyPrice * months;
        const savings = ((totalMonthlyPrice - periodPrice) / totalMonthlyPrice) * 100;
        return Math.round(savings);
    };

    const calculateMonthlyPrice = (amount, months) => {
        if (!amount || !months) return 0;
        return amount / months;
    };

    const formatPriceWithPeriod = (amount, period) => {
        if (!amount) return '';
        const formattedPrice = formatPrice(amount/100);
        return `${formattedPrice}${period ? ` / ${period}` : ''}`;
    };

    const formatMonthlyPrice = (amount, months) => {
        if (!amount || !months) return '';
        const monthlyAmount = amount/100 / months;
        return `${formatPrice(monthlyAmount)} / month`;
    };

    if (loading) {
        return "Loading...";
    }

    console.log("Prices received:", prices); // Debug log

    const handleCheckout = async (plan) => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        try {
            setCheckoutLoading(true);
            const response = await api.post(`/checkout/${plan.id}`);
            
            if (response.data && response.data.checkout_url) {
                // Redirection vers l'URL de checkout Stripe
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            console.error('Error during checkout:', error);
        } finally {
            setCheckoutLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <div className="pricing-page">
                <div className="pricing-header">
                    <h1>Pricing</h1>
                    <p>Choose your subscription to get more benefits and save up to 74%</p>
                </div>

                {/* DealFuel Lifetime Deal banner */}
                <DealfioulBanner />

                <div className="pricing-grid">
                    <div className="pricing-card">
                        <div className="plan-header">
                            <h2>Free</h2>
                            <div className="price-container">
                                <h3>Free<span className="period"></span></h3>
                                <p className="monthly-price">No reduction</p>
                            </div>
                        </div>
                        <ul className="features-list">
                            <li><span className="material-symbols-outlined icon">check</span>Table of contents creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Content creation and editing</li>
                            <li className="disabled"><span className="material-symbols-outlined icon">close</span>Unlimited Writing</li>
                            <li className="disabled"><span className="material-symbols-outlined icon">close</span>Fast Ebook Generator</li>
                            <li className="disabled"><span className="material-symbols-outlined icon">close</span>Advanced Editing Tools</li>
                            <li className="disabled"><span className="material-symbols-outlined icon">close</span>Multilingual Support (30+ languages)</li>
                            <li className="disabled"><span className="material-symbols-outlined icon">close</span>File Import and Export</li>
                            <li className="disabled"><span className="material-symbols-outlined icon">close</span>Priority Support</li>
                        </ul>
                    </div>

                    <div className="pricing-card">
                        <div className="discount">10% off</div>
                        <div className="plan-header">
                            <h2>Monthly</h2>
                            <div className="price-container">
                                <h3>{formatPriceWithPeriod(prices?.monthly?.unit_amount, 'month')}</h3>
                                <p className="monthly-price">{formatMonthlyPrice(prices?.monthly?.unit_amount, 1)}</p>
                            </div>
                        </div>
                        <ul className="features-list">
                            <li><span className="material-symbols-outlined icon">check</span>Table of contents creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Content creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Unlimited Writing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Fast Ebook Generator</li>
                            <li><span className="material-symbols-outlined icon">check</span>Advanced Editing Tools</li>
                            <li><span className="material-symbols-outlined icon">check</span>Multilingual Support (30+ languages)</li>
                            <li><span className="material-symbols-outlined icon">check</span>File Import and Export</li>
                            <li><span className="material-symbols-outlined icon">check</span>Priority Support</li>
                        </ul>
                        <LoadingButton
                            isLoading={checkoutLoading}
                            onClick={() => handleCheckout(prices?.monthly)}
                            className="button-plain"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            Upgrade to Unlimited Writing
                        </LoadingButton>
                    </div>

                    <div className="pricing-card">
                        <div className="discount">{calculateSavings(prices?.quarterly?.unit_amount, prices?.monthly?.unit_amount, 3)}% off</div>
                        <div className="plan-header">
                            <h2>Quarterly</h2>
                            <div className="price-container">
                                <h3>{formatPriceWithPeriod(prices?.quarterly?.unit_amount, 'quarter')}</h3>
                                <p className="monthly-price">{formatMonthlyPrice(prices?.quarterly?.unit_amount, 3)}</p>
                            </div>
                        </div>
                        <ul className="features-list">
                            <li><span className="material-symbols-outlined icon">check</span>Table of contents creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Content creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Unlimited Writing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Fast Ebook Generator</li>
                            <li><span className="material-symbols-outlined icon">check</span>Advanced Editing Tools</li>
                            <li><span className="material-symbols-outlined icon">check</span>Multilingual Support (30+ languages)</li>
                            <li><span className="material-symbols-outlined icon">check</span>File Import and Export</li>
                            <li><span className="material-symbols-outlined icon">check</span>Priority Support</li>
                        </ul>
                        <LoadingButton
                            isLoading={checkoutLoading}
                            onClick={() => handleCheckout(prices?.quarterly)}
                            className="button-plain"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            Upgrade to Unlimited Writing
                        </LoadingButton>
                    </div>

                    <div className="pricing-card best-value">
                        <div className="tag">Best value</div>
                        <div className="discount">{calculateSavings(prices?.yearly?.unit_amount, prices?.monthly?.unit_amount, 12)}% off</div>
                        <div className="plan-header">
                            <h2>Annual</h2>
                            <div className="price-container">
                                <h3>{formatPriceWithPeriod(prices?.yearly?.unit_amount, 'year')}</h3>
                                <p className="monthly-price">{formatMonthlyPrice(prices?.yearly?.unit_amount, 12)}</p>
                            </div>
                        </div>
                        <ul className="features-list">
                            <li><span className="material-symbols-outlined icon">check</span>Table of contents creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Content creation and editing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Unlimited Writing</li>
                            <li><span className="material-symbols-outlined icon">check</span>Fast Ebook Generator</li>
                            <li><span className="material-symbols-outlined icon">check</span>Advanced Editing Tools</li>
                            <li><span className="material-symbols-outlined icon">check</span>Multilingual Support (30+ languages)</li>
                            <li><span className="material-symbols-outlined icon">check</span>File Import and Export</li>
                            <li><span className="material-symbols-outlined icon">check</span>Priority Support</li>
                        </ul>
                        <LoadingButton
                            isLoading={checkoutLoading}
                            onClick={() => handleCheckout(prices?.yearly)}
                            className="button-plain"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            Upgrade to Unlimited Writing
                        </LoadingButton>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default PricingPage; 